<template>
  <div class="expense-page">
    <div class="page-header">
      <div>
        <h1>Expense Track - Transactions</h1>
        <p>Track income and expense records</p>
      </div>
      <button class="btn btn-primary" @click="openCreate">Create Transaction</button>
    </div>

    <div class="kpis">
      <div class="kpi"><span>Income</span><strong>{{ fmt(summary.income) }}</strong></div>
      <div class="kpi"><span>Expense</span><strong>{{ fmt(summary.expense) }}</strong></div>
      <div class="kpi"><span>Balance</span><strong>{{ fmt(summary.balance) }}</strong></div>
      <div class="kpi"><span>Rent Income</span><strong>{{ fmt(summary.rentincome) }}</strong></div>
    </div>

    <div class="filters">
      <input class="input" type="date" v-model="dateInputs.fromdate" />
      <input class="input" type="date" v-model="dateInputs.todate" />
      <button class="mini mini-apply" @click="applyDateFilter">Apply Date</button>
      <button class="mini mini-clear" @click="clearDateFilter">Clear Date</button>
      <select class="input" v-model="filters.type" @change="handleTypeChange">
        <option value="">All Types</option>
        <option value="INCOME">INCOME</option>
        <option value="EXPENSE">EXPENSE</option>
      </select>
      <select class="input" v-model="filters.categoryid" @change="fetchTransactions">
        <option value="">All Categories</option>
        <option v-for="c in categoryOptionsForFilter" :key="c.categoryid || c.id" :value="c.categoryid || c.id">{{ c.name }}</option>
      </select>
      <select class="input" v-model="filters.paymentmode" @change="fetchTransactions">
        <option value="">All Payment Modes</option>
        <option value="CASH">CASH</option>
        <option value="ONLINE">ONLINE</option>
      </select>
      <input class="input" type="text" placeholder="Reference type" v-model.trim="filters.referencetype" @input="debouncedFetch" />
      <input class="input" type="text" placeholder="Reference id" v-model.trim="filters.referenceid" @input="debouncedFetch" />
    </div>

    <div class="table-wrap">
      <div v-if="loading" class="table-loading-overlay">
        <div class="table-spinner">
          <div class="spinner-circle-small"></div>
          <span class="loading-text-small">Loading transactions...</span>
        </div>
      </div>
      <table v-if="rows.length > 0" class="table">
        <thead>
          <tr>
            <th>Date</th><th>Type</th><th>Category</th><th>Amount</th><th>Payment Mode</th><th>Description</th><th>Reference</th><th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.transactionsid || row.transactionid || row.id">
            <td>{{ formatDate(row.txndate || row.createdat) }}</td>
            <td>{{ row.type || '--' }}</td>
            <td>{{ row.categoryname || row.categoryid || '--' }}</td>
            <td>{{ fmt(row.amount) }}</td>
            <td>{{ row.paymentmode || '--' }}</td>
            <td>{{ row.description || '--' }}</td>
            <td>{{ row.referencetype || '--' }} {{ row.referenceid ? `(${row.referenceid})` : '' }}</td>
            <td class="actions">
              <button class="mini" @click="openEdit(row)">Edit</button>
              <button class="mini danger" @click="askDelete(row)">Delete</button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!loading" class="empty-state">
        <img src="@/assets/guest-maint-staff.svg" alt="No transactions illustration" class="empty-illustration-img" />
        <p>No transactions found</p>
      </div>
    </div>

    <div class="pagination-wrapper">
      <button class="pagination-btn" :disabled="filters.page <= 1" @click="changePage(filters.page - 1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
      </button>
      <button v-for="page in visiblePages" :key="page" class="pagination-btn" :class="{ active: page === filters.page }" @click="changePage(page)">
        {{ page }}
      </button>
      <button class="pagination-btn" :disabled="filters.page >= totalPages" @click="changePage(filters.page + 1)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </button>
    </div>

    <div v-if="showFormModal || showViewModal" class="modal-overlay" @click.self="closeForm">
      <div class="modal">
        <h3>{{ formMode === 'view' ? 'Transaction Details' : formMode === 'edit' ? 'Edit Transaction' : 'Create Transaction' }}</h3>
        <div class="form-grid">
          <label>Type</label>
          <select class="input" v-model="form.type" :disabled="formMode === 'view'" @change="syncCategoryOptionsForForm">
            <option value="">Select</option>
            <option value="INCOME">INCOME</option>
            <option value="EXPENSE">EXPENSE</option>
          </select>
          <label>Category</label>
          <select class="input" v-model="form.categoryid" :disabled="formMode === 'view'">
            <option value="">Select</option>
            <option v-for="c in categoryOptionsForForm" :key="c.categoryid || c.id" :value="c.categoryid || c.id">{{ c.name }}</option>
          </select>
          <label>Amount</label>
          <input class="input" type="number" min="0" step="0.01" v-model.number="form.amount" :disabled="formMode === 'view'" />
          <label>Payment Mode</label>
          <select class="input" v-model="form.paymentmode" :disabled="formMode === 'view'">
            <option value="CASH">CASH</option>
            <option value="ONLINE">ONLINE</option>
          </select>
          <label>Description</label>
          <input class="input" v-model.trim="form.description" :disabled="formMode === 'view'" />
        </div>
        <div class="modal-actions">
          <button class="mini" @click="closeForm">Close</button>
          <button v-if="formMode !== 'view'" class="btn btn-primary" :disabled="saving" @click="saveTransaction">{{ saving ? 'Saving...' : 'Save' }}</button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteModal" class="modal-overlay" @click.self="showDeleteModal = false">
      <div class="modal">
        <h3>Delete Transaction</h3>
        <p>This action cannot be undone.</p>
        <div class="modal-actions">
          <button class="mini" @click="showDeleteModal = false">Cancel</button>
          <button class="mini danger" :disabled="saving" @click="deleteTransaction">Delete</button>
        </div>
      </div>
    </div>

    <div v-if="toast.message" :class="['toast', toast.type]">{{ toast.message }}</div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '@/utilities/errorHelper';

export default {
  name: 'ExpenseTransactionsView',
  data() {
    return {
      rows: [],
      loading: false,
      saving: false,
      categories: [],
      summary: { income: 0, expense: 0, balance: 0, rentincome: 0 },
      totalCount: 0,
      filters: {
        page: 1, pagesize: 10, type: '', categoryid: '', paymentmode: '', referencetype: '', referenceid: '', fromdate: '', todate: '',
      },
      dateInputs: { fromdate: '', todate: '' },
      searchTimer: null,
      requestSeq: 0,
      showFormModal: false,
      showViewModal: false,
      showDeleteModal: false,
      selectedRow: null,
      formMode: 'create',
      form: { type: '', categoryid: '', amount: '', paymentmode: 'CASH', description: '' },
      toast: { message: '', type: 'success' },
    };
  },
  computed: {
    objectId() { return this.$route.params.id || this.$route.query.objectid || ''; },
    categoryOptionsForFilter() {
      return this.categories.filter((c) => !this.filters.type || c.type === this.filters.type);
    },
    categoryOptionsForForm() {
      return this.categories.filter((c) => !this.form.type || c.type === this.form.type);
    },
    totalPages() {
      if (this.totalCount > 0) {
        return Math.max(1, Math.ceil(this.totalCount / this.filters.pagesize));
      }
      if (this.rows.length === 0) return Math.max(1, this.filters.page);
      return this.rows.length < this.filters.pagesize ? this.filters.page : this.filters.page + 1;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 4;
      let start = Math.max(1, this.filters.page - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i += 1) pages.push(i);
      return pages;
    },
    summaryDateRange() {
      if (this.filters.fromdate && this.filters.todate) {
        return { fromdate: this.filters.fromdate, todate: this.filters.todate };
      }
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      return {
        fromdate: `${year}-${month}-01`,
        todate: `${year}-${month}-${day}`,
      };
    },
  },
  mounted() {
    this.refreshAll();
  },
  methods: {
    fmt(v) {
      const n = Number(v || 0);
      return new Intl.NumberFormat('en-NP', { style: 'currency', currency: 'NPR', maximumFractionDigits: 2 }).format(n);
    },
    formatDate(v) {
      if (!v) return '--';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return String(v);
      return d.toLocaleDateString('en-NP', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    showToast(message, type = 'success') {
      this.toast = { message, type };
      window.setTimeout(() => { if (this.toast.message === message) this.toast.message = ''; }, 3000);
    },
    debouncedFetch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.fetchTransactions();
      }, 350);
    },
    buildParams() {
      const p = { ...this.filters };
      Object.keys(p).forEach((k) => {
        if (p[k] === '' || p[k] == null) delete p[k];
      });
      return p;
    },
    async refreshAll() {
      await this.fetchCategories();
      await Promise.all([this.fetchTransactions(), this.fetchSummary()]);
    },
    async fetchCategories() {
      if (!this.objectId) return;
      try {
        const response = await apiService.getExpenseCategories(this.objectId);
        const data = response?.data?.data;
        this.categories = Array.isArray(data) ? data : Array.isArray(response?.data) ? response.data : [];
      } catch (error) {
        if (!isNoDataError(error) && !isHmsSubscriptionInactiveError(error)) {
          this.showToast('Failed to load categories', 'error');
        }
      }
    },
    async fetchTransactions() {
      if (!this.objectId) return;
      this.loading = true;
      const seq = ++this.requestSeq;
      try {
        const response = await apiService.getExpenseTransactions(this.objectId, this.buildParams());
        if (seq !== this.requestSeq) return;
        const data = response?.data?.data;
        this.rows = Array.isArray(data) ? data : Array.isArray(response?.data) ? response.data : [];
        this.totalCount = Number(response?.data?.totalCount || response?.data?.totalcount || 0);
      } catch (error) {
        if (isNoDataError(error)) {
          this.rows = [];
          this.totalCount = 0;
        } else if (!isHmsSubscriptionInactiveError(error)) {
          this.showToast(error?.response?.data?.reason || 'Failed to load transactions', 'error');
        }
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    async fetchSummary() {
      if (!this.objectId) return;
      try {
        const response = await apiService.getExpenseTransactionSummary(this.objectId, this.summaryDateRange);
        const data = response?.data?.data || response?.data || {};
        this.summary = {
          income: Number(data.income || 0),
          expense: Number(data.expense || 0),
          balance: Number(data.balance || 0),
          rentincome: Number(data.rentincome || data.rentIncome || 0),
        };
      } catch (error) {
        if (!isNoDataError(error) && !isHmsSubscriptionInactiveError(error)) {
          this.showToast('Failed to load summary', 'error');
        }
      }
    },
    handleTypeChange() {
      this.filters.categoryid = '';
      this.filters.page = 1;
      this.fetchTransactions();
    },
    applyDateFilter() {
      this.filters.fromdate = this.dateInputs.fromdate || '';
      this.filters.todate = this.dateInputs.todate || '';
      this.filters.page = 1;
      this.refreshAll();
    },
    clearDateFilter() {
      this.dateInputs = { fromdate: '', todate: '' };
      this.filters.fromdate = '';
      this.filters.todate = '';
      this.filters.page = 1;
      this.refreshAll();
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages || page === this.filters.page) return;
      this.filters.page = page;
      this.fetchTransactions();
    },
    syncCategoryOptionsForForm() {
      if (!this.categoryOptionsForForm.some((c) => (c.categoryid || c.id) === this.form.categoryid)) {
        this.form.categoryid = '';
      }
    },
    openCreate() {
      this.formMode = 'create';
      this.form = { type: '', categoryid: '', amount: '', paymentmode: 'CASH', description: '' };
      this.showFormModal = true;
      this.showViewModal = false;
    },
    openEdit(row) {
      this.formMode = 'edit';
      this.selectedRow = row;
      this.form = {
        type: row.type || '',
        categoryid: row.categoryid || '',
        amount: row.amount || '',
        paymentmode: row.paymentmode || 'CASH',
        description: row.description || '',
      };
      this.showFormModal = true;
      this.showViewModal = false;
    },
    openView(row) {
      this.formMode = 'view';
      this.selectedRow = row;
      this.form = {
        type: row.type || '',
        categoryid: row.categoryid || '',
        amount: row.amount || '',
        paymentmode: row.paymentmode || 'CASH',
        description: row.description || '',
      };
      this.showViewModal = true;
      this.showFormModal = false;
    },
    closeForm() {
      this.showFormModal = false;
      this.showViewModal = false;
    },
    async saveTransaction() {
      if (!this.form.type || !this.form.categoryid || Number(this.form.amount) <= 0) {
        this.showToast('Type, category and amount (> 0) are required', 'error');
        return;
      }
      this.saving = true;
      try {
        const payload = {
          type: this.form.type,
          categoryid: this.form.categoryid,
          amount: Number(this.form.amount),
          paymentmode: this.form.paymentmode || 'CASH',
          description: this.form.description || undefined,
        };
        if (this.formMode === 'edit' && this.selectedRow) {
          const id = this.selectedRow.transactionsid || this.selectedRow.transactionid || this.selectedRow.id;
          await apiService.updateExpenseTransaction(this.objectId, id, payload);
          this.showToast('Transaction updated');
        } else {
          await apiService.createExpenseTransaction(this.objectId, payload);
          this.showToast('Transaction created');
        }
        this.closeForm();
        await this.fetchTransactions();
        await this.fetchSummary();
      } catch (error) {
        this.showToast(error?.response?.data?.reason || 'Unable to save transaction', 'error');
      } finally {
        this.saving = false;
      }
    },
    askDelete(row) {
      this.selectedRow = row;
      this.showDeleteModal = true;
    },
    async deleteTransaction() {
      if (!this.selectedRow) return;
      this.saving = true;
      try {
        const id = this.selectedRow.transactionsid || this.selectedRow.transactionid || this.selectedRow.id;
        await apiService.deleteExpenseTransaction(this.objectId, id);
        this.showToast('Transaction deleted');
        this.showDeleteModal = false;
        await this.fetchTransactions();
        await this.fetchSummary();
      } catch (error) {
        this.showToast(error?.response?.data?.reason || 'Unable to delete transaction', 'error');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.expense-page { padding: 20px; background: #f8fafc; min-height: 100%; }
.page-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; }
.page-header h1 { margin: 0; font-size: 20px; color: #1e293b; }
.page-header p { margin: 0; color: #64748b; font-size: 13px; }
.btn { border: 0; border-radius: 8px; padding: 10px 14px; font-weight: 600; cursor: pointer; }
.btn-primary { background: linear-gradient(135deg, #5b2e8e, #7c3aed); color: #fff; }
.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 12px; }
.kpi { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; }
.kpi span { display: block; font-size: 12px; color: #64748b; }
.kpi strong { display: block; font-size: 15px; color: #1e293b; margin-top: 4px; }
.filters { display: grid; grid-template-columns: repeat(9, minmax(110px, 1fr)); gap: 8px; margin-bottom: 12px; }
.input { padding: 10px 12px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; }
.mini { border: 1px solid #e2e8f0; background: #fff; border-radius: 8px; padding: 7px 10px; cursor: pointer; }
.mini-apply {
  background: linear-gradient(135deg, #5b2e8e, #7c3aed);
  border-color: #5b2e8e;
  color: #fff;
}
.mini-clear {
  background: #f3e8ff;
  border-color: #d8b4fe;
  color: #6b21a8;
}
.danger { color: #b91c1c; border-color: #fecaca; background: #fff5f5; }
.table-wrap { position: relative; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto; min-height: 220px; }
.table { width: 100%; border-collapse: collapse; }
.table th, .table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; text-align: left; font-size: 13px; white-space: nowrap; }
.table th { background: #f1f5f9; color: #475569; font-weight: 600; }
.actions { display: flex; gap: 6px; }
.table-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.table-spinner { display: flex; align-items: center; gap: 10px; padding: 14px 18px; background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
.spinner-circle-small { width: 20px; height: 20px; border: 2px solid #e2e8f0; border-top: 2px solid #5b2e8e; border-radius: 50%; animation: spin 1s linear infinite; }
.loading-text-small { color: #6b7280; font-size: 14px; font-weight: 500; }
.empty-state { padding: 32px 16px; text-align: center; color: #64748b; }
.empty-illustration-img { width: 220px; height: 220px; max-width: 100%; object-fit: contain; margin-bottom: 12px; }
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}
.pagination-btn {
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}
.pagination-btn:hover:not(:disabled) { border-color: #5b2e8e; color: #5b2e8e; }
.pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; background: #f8fafc; }
.pagination-btn.active { background: linear-gradient(135deg, #5b2e8e, #7c3aed); color: white; border-color: #5b2e8e; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { width: min(640px, 94vw); background: #fff; border-radius: 12px; padding: 16px; }
.form-grid { display: grid; grid-template-columns: 130px 1fr; gap: 10px; align-items: center; margin: 12px 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.toast { position: fixed; right: 16px; top: 84px; padding: 10px 12px; border-radius: 8px; color: #fff; z-index: 1300; }
.toast.success { background: #16a34a; }
.toast.error { background: #dc2626; }
@keyframes spin { to { transform: rotate(360deg); } }
@media (max-width: 1100px) { .filters { grid-template-columns: repeat(3, 1fr); } .kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 768px) { .filters, .kpis { grid-template-columns: 1fr; } }
</style>
